import {
  ClearBehaviour,
  GroupBox,
  Labeled,
  LabeledProps,
  NumericInput,
  Select,
  TextInput,
} from '@infotech-soe/erp-ui-kit';
import { TDatabaseId, tg } from '@infotech-soe/metaschema';
import { produce } from 'immer';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { WorkTableData } from '../../../../models/work-table/WorkTableData';
import { WorkTableType } from '../../../../services/commands/UserInteractor';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getWorkTableState } from '../../../../store/selectors/workTable';
import workTable, {
  OpenWorkTablePostEffectType,
} from '../../../../store/slices/workTable';
import WorkDays from '../tables/days/WorkDays';
import WorkHours from '../tables/hours/WorkHours';

interface Props {
  type?: 'calendar' | 'workTime' | 'planTime';
  data: WorkTableData;
  fullName?: string;
  tellerNumber?: string;
  month: number;
  tableId: TDatabaseId;
  year: number;
  scheduleCode?: string | null;
  hasContextMenu?: boolean;
  onPeriodChange?: (event: ChangeEvent<HTMLInputElement>) => void;
}

const enum InfoPropsType {
  TEXT = 'text',
  NUMBER = 'number',
}

interface InfoProps extends Pick<LabeledProps, 'childColumns'> {
  label?: string;
  type: InfoPropsType;
  value?: string | number;
  className: string;
}

interface InputProps {
  type: InfoPropsType;
  value?: string | number;
}

const messages = defineMessages({
  norm: {
    id: 'core.worktable.norm',
    defaultMessage: 'Norma',
  },
  requisites: {
    id: 'core.worktable.requisites',
    defaultMessage: 'Requisites',
  },
  employee: {
    id: 'core.worktable.employee',
    defaultMessage: 'Requisites',
  },
  employeeNumber: {
    id: 'core.worktable.employeeNumber',
    defaultMessage: 'Teller Number',
  },
  period: {
    id: 'core.worktable.period',
    defaultMessage: 'Period',
  },
  workSchedule: {
    id: 'core.worktable.workSchedule',
    defaultMessage: 'Work schedule',
  },
  plan: {
    id: 'core.worktable.plan',
    defaultMessage: 'Plan',
  },
  fact: {
    id: 'core.worktable.fact',
    defaultMessage: 'Fact',
  },
  hours: {
    id: 'core.worktable.hours',
    defaultMessage: 'Hours',
  },
  days: {
    id: 'core.worktable.days',
    defaultMessage: 'Days',
  },
  month: {
    id: 'core.date.month',
    defaultMessage: 'Month',
  },
  months: {
    id: 'core.date.months',
    defaultMessage: 'Month',
  },
  year: {
    id: 'core.date.year',
    defaultMessage: 'Year',
  },
  timeBoard: {
    id: 'core.worktable.timeBoard',
    defaultMessage: 'Time board',
  },
});

const WorkTimeInput = React.forwardRef(
  (props: InputProps, ref: React.Ref<HTMLInputElement>) => {
    if (props.type === InfoPropsType.NUMBER && tg.isNumber(props.value)) {
      return <NumericInput value={props.value} readOnly ref={ref} />;
    } else {
      return <TextInput value={String(props.value)} readOnly ref={ref} />;
    }
  }
);
WorkTimeInput.displayName = 'WorkTimeInput';

const WorkTimeInfo: React.FC<InfoProps> = props => (
  <Labeled
    label={props.label}
    labelPosition="left"
    labelVerticalAlign="center"
    childColumns={props.childColumns}
    className={props.className}
  >
    <WorkTimeInput type={props.type} value={props.value} />
  </Labeled>
);

const WorkTableForm: React.FC<Props> = props => {
  const intl = useIntl();
  const [state, setState] = useState({
    year: props.year,
    month: props.month,
    dateChanged: false,
    fact: {
      days: 0,
      hours: 0,
    },
  });

  const dispatch = useAppDispatch();
  const { datasetId, tableType } = useAppSelector(getWorkTableState);

  const EmployeeInfo = (): React.JSX.Element =>
    tableType === WorkTableType.CALENDAR ? (
      <div />
    ) : (
      <div className="row gutter-x-md">
        <WorkTimeInfo
          className="col-8"
          childColumns={9}
          label={intl.formatMessage(messages.employee)}
          type={InfoPropsType.TEXT}
          value={props.fullName}
        />
        <WorkTimeInfo
          className="col-4"
          childColumns={6}
          label={intl.formatMessage(messages.employeeNumber)}
          type={InfoPropsType.TEXT}
          value={props.tellerNumber}
        />
      </div>
    );

  const monthChangeHandler = (value: number | null): void => {
    if (value === null) return;
    setState(
      produce(newState => {
        newState.month = value;
        newState.dateChanged = true;
      })
    );
  };
  const yearChangeHandler = (value: number | null): void => {
    if (value === null) return;
    setState(
      produce(newState => {
        newState.year = value;
        newState.dateChanged = true;
      })
    );
  };

  const CalendarInfo = (): React.JSX.Element => {
    if (tableType === WorkTableType.CALENDAR) {
      return (
        <div className="row gutter-x-md m-mt-md">
          <WorkTimeInfo
            className="col-3"
            childColumns={6}
            label={intl.formatMessage(messages.month)}
            type={InfoPropsType.TEXT}
            value={intl.formatMessage(messages.months).split('_')[props.month]}
          />
          <WorkTimeInfo
            className="col-3"
            childColumns={6}
            label={intl.formatMessage(messages.year)}
            type={InfoPropsType.TEXT}
            value={props.year}
          />
          <WorkTimeInfo
            className="col-6"
            childColumns={8}
            label={intl.formatMessage(messages.workSchedule)}
            type={InfoPropsType.TEXT}
            value={props.scheduleCode || ''}
          />
        </div>
      );
    } else if (tableType === WorkTableType.TIME_BOARD) {
      return (
        <div className="row gutter-x-md m-mt-md">
          <WorkTimeInfo
            className="col-8"
            childColumns={9}
            label={intl.formatMessage(messages.month)}
            type={InfoPropsType.TEXT}
            value={intl.formatMessage(messages.months).split('_')[props.month]}
          />
          <WorkTimeInfo
            className="col-4"
            childColumns={6}
            label={intl.formatMessage(messages.year)}
            type={InfoPropsType.TEXT}
            value={props.year}
          />
        </div>
      );
    } else {
      return (
        <div className="row gutter-x-md m-mt-md">
          <Labeled
            label={intl.formatMessage(messages.month)}
            labelPosition="left"
            labelVerticalAlign="center"
            childColumns={7}
            className="col-4"
          >
            <Select
              onChange={monthChangeHandler}
              options={intl
                .formatMessage(messages.months)
                .split('_')
                .map((v, i) => ({ type: 'option', value: i, label: v }))}
              value={props.month}
              withClear={ClearBehaviour.NONE}
            />
          </Labeled>
          <Labeled
            label={intl.formatMessage(messages.year)}
            labelPosition="left"
            labelVerticalAlign="center"
            childColumns={7}
            className="col-3"
          >
            <NumericInput
              value={props.year}
              step={1}
              onChange={yearChangeHandler}
            />
          </Labeled>
          <WorkTimeInfo
            className="col-5"
            childColumns={8}
            label={intl.formatMessage(messages.workSchedule)}
            type={InfoPropsType.TEXT}
            value={props.scheduleCode || ''}
          />
        </div>
      );
    }
  };

  const TotalInfo = (): React.JSX.Element => {
    if (tableType === WorkTableType.CALENDAR) {
      return (
        <GroupBox label={intl.formatMessage(messages.plan)} className="col-3">
          <div className="row gutter-x-md m-mt-md">
            <Labeled
              label={intl.formatMessage(messages.days)}
              labelPosition="left"
              labelVerticalAlign="center"
              childColumns={6}
              className="col-6"
            >
              <NumericInput value={state.fact.days || 0} readOnly />
            </Labeled>
            <Labeled
              label={intl.formatMessage(messages.hours)}
              labelPosition="left"
              labelVerticalAlign="center"
              childColumns={6}
              className="col-6"
            >
              <NumericInput
                precision={2}
                value={state.fact.hours || 0}
                readOnly
              />
            </Labeled>
          </div>
        </GroupBox>
      );
    } else if (tableType === WorkTableType.TIME_BOARD) {
      return (
        <GroupBox label={intl.formatMessage(messages.fact)} className="col-3">
          <div className="row gutter-x-md">
            <Labeled
              label={intl.formatMessage(messages.days)}
              labelPosition="left"
              labelVerticalAlign="center"
              childColumns={6}
              className="col-6"
            >
              <NumericInput value={state.fact.days || 0} readOnly />
            </Labeled>
            <Labeled
              label={intl.formatMessage(messages.hours)}
              labelPosition="left"
              labelVerticalAlign="center"
              childColumns={6}
              className="col-6"
            >
              <NumericInput
                precision={2}
                value={state.fact.hours || 0}
                readOnly
              />
            </Labeled>
          </div>
        </GroupBox>
      );
    } else {
      return (
        <GroupBox
          label={
            intl.formatMessage(messages.norm) +
            '/' +
            intl.formatMessage(
              tableType === WorkTableType.FACT ? messages.fact : messages.plan
            )
          }
          className="col-3"
        >
          <div className="row gutter-x-md">
            <Labeled
              label={intl.formatMessage(messages.hours)}
              labelPosition="left"
              labelVerticalAlign="center"
              childColumns={5}
              className="col-7"
            >
              <NumericInput
                precision={2}
                value={props.data.norm.hours || 0}
                readOnly
              />
            </Labeled>
            <Labeled labelPosition="left" childColumns={9} className="col-5">
              <NumericInput
                precision={2}
                value={state.fact.hours || 0}
                readOnly
              />
            </Labeled>
          </div>
          <div className="row gutter-x-md  m-mt-md">
            <WorkTimeInfo
              className="col-7"
              childColumns={5}
              label={intl.formatMessage(messages.days)}
              type={InfoPropsType.NUMBER}
              value={props.data.norm.days || 0}
            />
            <Labeled labelPosition="left" childColumns={9} className="col-5">
              <NumericInput value={state.fact.days || 0} readOnly />
            </Labeled>
          </div>
        </GroupBox>
      );
    }
  };

  useEffect(() => {
    if (state.dateChanged) {
      setState(
        produce(newState => {
          newState.dateChanged = false;
        })
      );
      dispatch(
        workTable.open({
          datasetId,
          tableType,
          effect: {
            type: OpenWorkTablePostEffectType.CHANGE_DATE,
            workerId: props.tableId,
            newDate: { month: state.month, year: state.year },
          },
        })
      );
    }
  }, [
    state.year,
    state.month,
    state.dateChanged,
    dispatch,
    datasetId,
    tableType,
    props.tableId,
  ]);

  return (
    <div className="column m-pa-sm">
      <div className="row">
        <GroupBox
          label={intl.formatMessage(messages.requisites)}
          className="col-9"
        >
          <EmployeeInfo />
          <CalendarInfo />
        </GroupBox>
        <TotalInfo />
      </div>
      <div className="row">
        <WorkHours
          hasContextMenu={props.hasContextMenu}
          workTableData={props.data}
          tableId={props.tableId}
          onUpdateHours={(totalHours: number, totalDays: number) =>
            setState(
              produce(newState => {
                newState.fact.hours = totalHours;
                newState.fact.days = totalDays;
              })
            )
          }
        />
      </div>
      <div className="row">
        <WorkDays
          hasContextMenu={props.hasContextMenu}
          workTableData={props.data}
          year={props.year}
          month={props.month}
          tableId={props.tableId}
          onUpdateDays={(totalHours: number, totalDays: number) =>
            setState(
              produce(newState => {
                newState.fact.hours = totalHours;
                newState.fact.days = totalDays;
              })
            )
          }
        />
      </div>
    </div>
  );
};

export default WorkTableForm;
